import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';

interface SnackbarContextType {
  openSnackbar: (message: string, severity: AlertProps['severity']) => void;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertProps['severity'];
  /** Bumped on every call so the same message twice in a row still re-shows. */
  key: number;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SnackbarState>({ open: false, message: '', severity: 'info', key: 0 });

  // Stable identity: without it every message re-rendered every consumer in the app.
  const openSnackbar = useCallback((message: string, severity: AlertProps['severity']) => {
    setState((previous) => ({ open: true, message, severity, key: previous.key + 1 }));
  }, []);

  const close = useCallback(() => setState((previous) => ({ ...previous, open: false })), []);

  const value = useMemo(() => ({ openSnackbar }), [openSnackbar]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        key={state.key}
        open={state.open}
        autoHideDuration={3000}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert elevation={6} variant="filled" onClose={close} severity={state.severity}>
          {state.message}
        </MuiAlert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
